import { DestroyRef, inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, shareReplay, take } from 'rxjs';
import { ApiService } from '../../api/services/api.service';
import { BannerDto, SurveyDto } from '../../api/models/fraud.models';
import { AuthService } from './auth.service';
import { DialogService } from './dialog.service';
import { isMeaningfulRewardAmount } from '../utils/survey.utils';
import { WelcomeBonusDialogData } from '../components/dialogs/dialog-welcome-bonus/dialog-welcome-bonus.component';

const WELCOME_BONUS_DISMISSED_KEY = 'welcomeBonusDismissed';

@Injectable({
  providedIn: 'root',
})
export class BonusSurveyService {
  private readonly _apiService = inject(ApiService);
  private readonly _authService = inject(AuthService);
  private readonly _dialogService = inject(DialogService);
  private readonly _matDialog = inject(MatDialog);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _surveyRequests = new Map<number, Observable<SurveyDto>>();

  constructor() {
    this._authService.accountSwitched$.pipe(
      takeUntilDestroyed(this._destroyRef),
    ).subscribe(() => {
      this.clearWelcomeBonusDismissed();
      this._surveyRequests.clear();
    });
  }

  loadSurvey(surveyId: number, options?: { forceRefresh?: boolean }): Observable<SurveyDto> {
    if (options?.forceRefresh) {
      this.invalidateSurveyCache(surveyId);
    }

    const cached = this._surveyRequests.get(surveyId);
    if (cached) {
      return cached;
    }

    const request = this._apiService.getSurvey(surveyId).pipe(
      shareReplay({ bufferSize: 1, refCount: true }),
    );
    this._surveyRequests.set(surveyId, request);
    return request;
  }

  invalidateSurveyCache(surveyId?: number): void {
    if (surveyId == null) {
      this._surveyRequests.clear();
      return;
    }

    this._surveyRequests.delete(surveyId);
  }

  isWelcomeBonusDismissed(): boolean {
    return localStorage.getItem(WELCOME_BONUS_DISMISSED_KEY) === 'true';
  }

  markWelcomeBonusDismissed(): void {
    localStorage.setItem(WELCOME_BONUS_DISMISSED_KEY, 'true');
  }

  clearWelcomeBonusDismissed(): void {
    localStorage.removeItem(WELCOME_BONUS_DISMISSED_KEY);
  }

  /** Opens the welcome modal once when a SURVEY banner is present (no survey fetch until Start). */
  tryShowWelcomeBonusDialog(banners: BannerDto[]): void {
    if (this.isWelcomeBonusDismissed() || this._isAnyDialogOpen()) {
      return;
    }

    const surveyBanner = [...banners]
      .filter((banner) => banner.bannerType === 'SURVEY')
      .sort((a, b) => (Number(b.priority) || 0) - (Number(a.priority) || 0))[0];
    const surveyId = surveyBanner?.payload?.surveyId;
    if (!surveyId) {
      return;
    }

    this.openWelcomeBonusDialog({
      surveyId,
      rewardAmount: surveyBanner.payload?.amount ?? this.getFallbackRewardAmount(),
    });
  }

  openWelcomeBonusDialog(data: WelcomeBonusDialogData): void {
    this._dialogService.openWelcomeBonusDialog(data).afterClosed().pipe(take(1)).subscribe((result) => {
      this.markWelcomeBonusDismissed();

      if (result === true) {
        this.openSurveyDialog(data.surveyId);
      }
    });
  }

  openSurveyDialog(surveyId: number): void {
    this.invalidateSurveyCache(surveyId);
    this._dialogService.openBonusSurveyDialog({ surveyId }).afterClosed().pipe(take(1)).subscribe({
      next: () => {
        this.invalidateSurveyCache(surveyId);
      },
    });
  }

  getFallbackRewardAmount(): number {
    return 0;
  }

  /** Survey rewardAmount takes priority; falls back to 0 when it isn't meaningful. */
  resolveDisplayRewardAmount(survey?: SurveyDto | null): string | number {
    if (survey && isMeaningfulRewardAmount(survey.rewardAmount)) {
      return survey.rewardAmount;
    }

    return this.getFallbackRewardAmount();
  }

  private _isAnyDialogOpen(): boolean {
    return this._matDialog.openDialogs.length > 0;
  }
}
